import {Button, Tooltip} from '@mantine/core';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import * as AssemblyConstants from 'constants/AssemblyConstants';
import {Cutsite} from 'constants/MolbioConstants';
import * as sequtil from 'molbio/SeqUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PopoverIcon from 'ui-library/PopoverIcon';

import {Assembly, AssemblySlotType, __Fragment} from './AssemblyModels';
import * as AssemblyUtil from './AssemblyUtil';

const {getSitesInsideFragmentWarning} = AssemblyUtil;

const {ASSEMBLY_TYPE} = AssemblyConstants;

const PREVIEW_EXTRA_BASES = 3;
const PREVIEW_PRIMER_BASES = 5;

const SIDE_LEFT = 'left-side';
const SIDE_RIGHT = 'right-side';
const CLASS_ACTIVE = 'site-preview-active';
const CLASS_MISMATCH = 'site-preview-mismatch';
const CLASS_INACTIVE = 'site-preview-inactive';
const CLASS_PRIMER = 'site-preview-primer';
const CLASS_INACTVE_PRIMER = 'site-preview-inactive-primer';
const CLASS_INFO_PREVIEW = 'current-fragment-info-preview';

interface SitePreviewProps {
  leftSlot: AssemblySlotType;
  rightSlot: AssemblySlotType;
  activeSide: 'left-side' | 'right-side';
}

class SitePreview extends React.Component<SitePreviewProps> {
  _fragmentBasesWithCutSite = (fragment: __Fragment): string => {
    const sequence = fragment.getSequence()!;
    const bases = (() => {
      if (fragment.enzymeIDs() == null) {
        return sequence.residueSlice(fragment.start()!, fragment.end()!);
      } else {
        // Expand start/end to include enzyme cutsites, if not included already.
        const [{offset: startOffset}, {offset: endOffset}] = Array.from(fragment.getEnzymes()!);
        // Allow start to expand left.
        const newStart = sequtil.mod(fragment.start()! + Math.min(0, startOffset), sequence.length());
        // Allow end to expand right.
        const newEnd = sequtil.mod(fragment.end()! + Math.max(0, endOffset), sequence.length());
        return sequence.residueSlice(newStart, newEnd);
      }
    })();
    if (fragment.reverse()) {
      return util.revcomp(bases, DNA_COMPLEMENT_TABLE);
    } else {
      return bases;
    }
  };

  _bothPrimers = (): boolean => {
    const leftPrimers =
      this.props.leftSlot.isEmpty() || this.props.leftSlot.singleFragment().enzymeIDs() == null;
    const rightPrimers =
      this.props.rightSlot.isEmpty() || this.props.rightSlot.singleFragment().enzymeIDs() == null;
    return leftPrimers && rightPrimers;
  };

  _filler = (n: number, char?: string): string => {
    if (char == null) {
      char = '-';
    }
    return __range__(0, n, false)
      .map((i) => char)
      .join('');
  };

  _getRightEnzyme = (
    rightFragment: __Fragment
  ): {
    color: string;
    name: string;
    offset: number;
  } => {
    const enzymeIndex = rightFragment.reverse() ? 1 : 0;
    return rightFragment.getEnzymes()![enzymeIndex];
  };

  _getLeftEnzyme = (
    leftFragment: __Fragment
  ): {
    color: string;
    name: string;
    offset: number;
  } => {
    const enzymeIndex = leftFragment.reverse() ? 0 : 1;
    return leftFragment.getEnzymes()![enzymeIndex];
  };

  renderLeft = (): JSX.Element => {
    let bases;

    const leftConstruct = (top: string, bottom: string): JSX.Element => {
      let className;
      if (this.props.activeSide === SIDE_LEFT) {
        className = CLASS_ACTIVE;
      } else {
        className = CLASS_INACTIVE;
      }
      return (
        <div className={CLASS_INFO_PREVIEW}>
          <div className={className}>{top}</div>
          <div className={className}>{bottom}</div>
        </div>
      );
    };

    const leftSlotSpacer = this.props.leftSlot.spacer();
    if (leftSlotSpacer) {
      const topBases = leftSlotSpacer.slice(-PREVIEW_EXTRA_BASES);
      return leftConstruct(topBases, util.complement(topBases, DNA_COMPLEMENT_TABLE));
    }

    if (this.props.leftSlot.isEmpty()) {
      return leftConstruct(this._filler(PREVIEW_EXTRA_BASES), this._filler(PREVIEW_EXTRA_BASES));
    }

    const leftFragment = this.props.leftSlot.singleFragment();
    const leftBases = this._fragmentBasesWithCutSite(leftFragment);
    if (this._bothPrimers()) {
      bases = leftBases.slice(-PREVIEW_PRIMER_BASES);
    } else {
      let offset;
      if (leftFragment.enzymeIDs() != null) {
        ({offset} = this._getLeftEnzyme(leftFragment));
      } else {
        offset = 0;
      }
      const sliceStart = -(PREVIEW_EXTRA_BASES + Math.abs(offset));
      let sliceEnd: number | undefined = -Math.abs(offset);
      // Need to not use sliceEnd if it's 0, otherwise e.g. [-5...0] won't work.
      if (sliceEnd === 0) {
        sliceEnd = undefined;
      }
      bases = leftBases.slice(sliceStart, sliceEnd);
    }
    return leftConstruct(bases, util.complement(bases, DNA_COMPLEMENT_TABLE));
  };

  renderMid = (): JSX.Element | null => {
    // No middle if both are primers.
    let bottomBases: string;
    let bottomLeftBases;
    let bottomRightBases;
    let classBottom;
    let classTop;
    let leftBases;
    let leftFragment: __Fragment;
    let offset;
    let otherBasesFunc;
    let otherClass;
    let rightBases;
    let rightFragment: __Fragment;
    let topBases: string;
    let topLeftBases;
    let topRightBases;
    if (this._bothPrimers()) {
      return null;
    }

    const midConstruct = (
      top: string,
      bottom: string,
      classTop: string,
      classBottom: string
    ): JSX.Element => (
      <div className={CLASS_INFO_PREVIEW}>
        <div className={classTop}>{top}</div>
        <div className={classBottom}>{bottom}</div>
      </div>
    );

    if (this.props.leftSlot.isEmpty()) {
      rightFragment = this.props.rightSlot.singleFragment();
      rightBases = this._fragmentBasesWithCutSite(rightFragment);
      ({offset} = this._getRightEnzyme(rightFragment));
      if (this.props.leftSlot.spacer()) {
        otherBasesFunc = (bases: string) => util.complement(bases, DNA_COMPLEMENT_TABLE);
        otherClass = CLASS_INACTVE_PRIMER;
      } else {
        otherBasesFunc = (bases: string) => this._filler(bases.length);
        otherClass = CLASS_INACTIVE;
      }
      if (offset >= 0) {
        topBases = rightBases.slice(0, offset);
        bottomBases = otherBasesFunc(topBases);
        classTop = this.props.activeSide === SIDE_RIGHT ? CLASS_ACTIVE : CLASS_INACTIVE;
        classBottom = otherClass;
      } else {
        bottomBases = util.complement(rightBases.slice(0, -offset), DNA_COMPLEMENT_TABLE);
        topBases = otherBasesFunc(bottomBases);
        classBottom = this.props.activeSide === SIDE_RIGHT ? CLASS_ACTIVE : CLASS_INACTIVE;
        classTop = otherClass;
      }
      return midConstruct(topBases, bottomBases, classTop, classBottom);
    }

    if (this.props.rightSlot.isEmpty()) {
      leftFragment = this.props.leftSlot.singleFragment();
      leftBases = this._fragmentBasesWithCutSite(leftFragment);
      ({offset} = this._getLeftEnzyme(leftFragment));
      if (this.props.rightSlot.spacer()) {
        otherBasesFunc = (bases: string) => util.complement(bases, DNA_COMPLEMENT_TABLE);
        otherClass = CLASS_INACTVE_PRIMER;
      } else {
        otherBasesFunc = (bases: string) => this._filler(bases.length);
        otherClass = CLASS_INACTIVE;
      }
      if (offset > 0) {
        bottomBases = util.complement(leftBases.slice(-offset), DNA_COMPLEMENT_TABLE);
        topBases = otherBasesFunc(bottomBases);
        classBottom = this.props.activeSide === SIDE_LEFT ? CLASS_ACTIVE : CLASS_INACTIVE;
        classTop = otherClass;
      } else if (offset === 0) {
        // Need to special case offset == 0 because [-offset...] above would grab ALL bases.
        topBases = '';
        bottomBases = '';
        classTop = CLASS_INACTIVE;
        classBottom = CLASS_INACTIVE;
      } else {
        topBases = leftBases.slice(offset);
        bottomBases = otherBasesFunc(topBases);
        classTop = this.props.activeSide === SIDE_LEFT ? CLASS_ACTIVE : CLASS_INACTIVE;
        classBottom = otherClass;
      }
      return midConstruct(topBases, bottomBases, classTop, classBottom);
    }

    leftFragment = this.props.leftSlot.singleFragment();
    rightFragment = this.props.rightSlot.singleFragment();
    leftBases = this._fragmentBasesWithCutSite(leftFragment);
    rightBases = this._fragmentBasesWithCutSite(rightFragment);
    if (leftFragment.enzymeIDs() != null) {
      ({offset} = this._getLeftEnzyme(leftFragment));
      if (rightFragment.enzymeIDs() != null) {
        const {offset: rightOffset} = this._getRightEnzyme(rightFragment);
        assert(rightOffset === offset, 'SitePreview only supports same offsets!');
      }
    } else {
      ({offset} = this._getRightEnzyme(rightFragment));
    }

    // Calculate left side.
    if (leftFragment.enzymeIDs() != null) {
      if (offset > 0) {
        topLeftBases = '';
        bottomLeftBases = leftBases.slice(-offset);
      } else if (offset === 0) {
        topLeftBases = '';
        bottomLeftBases = '';
      } else {
        topLeftBases = leftBases.slice(offset);
        bottomLeftBases = '';
      }
      // Primer will generate bases based on other fragment's cut site.
    } else if (offset >= 0) {
      topLeftBases = '';
      bottomLeftBases = rightBases.slice(0, offset);
    } else {
      topLeftBases = rightBases.slice(0, -offset);
      bottomLeftBases = '';
    }

    // Calculate right side.
    if (rightFragment.enzymeIDs() != null) {
      if (offset >= 0) {
        topRightBases = rightBases.slice(0, offset);
        bottomRightBases = '';
      } else {
        topRightBases = '';
        bottomRightBases = rightBases.slice(0, -offset);
      }
      // Primer will generate bases based on other fragment's cut site.
    } else if (offset > 0) {
      topRightBases = leftBases.slice(-offset);
      bottomRightBases = '';
    } else if (offset === 0) {
      topRightBases = '';
      bottomRightBases = '';
    } else {
      topRightBases = '';
      bottomRightBases = leftBases.slice(offset);
    }

    const topBasesLength = (topLeftBases + topRightBases).length;
    const bottomBasesLength = (bottomLeftBases + bottomRightBases).length;
    assert(topBasesLength === bottomBasesLength, 'Missing padding on top or bottom bases!');

    const getClassName = (side: string, fragment: __Fragment, mismatch: boolean): string => {
      if (this.props.activeSide === side) {
        if (mismatch) {
          return CLASS_MISMATCH;
        } else if (fragment.enzymeIDs() != null) {
          return CLASS_ACTIVE;
        } else {
          return CLASS_PRIMER;
        }
      } else if (fragment.enzymeIDs() != null) {
        return CLASS_INACTIVE;
      } else {
        return CLASS_INACTVE_PRIMER;
      }
    };
    topBases = topLeftBases + topRightBases;
    bottomBases = bottomLeftBases + bottomRightBases;
    return (
      <div className={CLASS_INFO_PREVIEW}>
        <div>
          {/* eslint-disable-next-line no-restricted-syntax */}
          {_.map(topBases, (base: string, i) => {
            const mismatch = base !== bottomBases[i];
            const side = i < topLeftBases.length ? SIDE_LEFT : SIDE_RIGHT;
            const fragment = i < topLeftBases.length ? leftFragment : rightFragment;
            return (
              <span key={i} className={getClassName(side, fragment, mismatch)}>
                {base}
              </span>
            );
          })}
        </div>
        <div>
          {/* eslint-disable-next-line no-restricted-syntax */}
          {_.map(bottomBases, (base, i) => {
            const mismatch = base !== topBases[i];
            const side = i < bottomLeftBases.length ? SIDE_LEFT : SIDE_RIGHT;
            const fragment = i < bottomLeftBases.length ? leftFragment : rightFragment;
            return (
              <span key={i} className={getClassName(side, fragment, mismatch)}>
                {util.revcomp(base, DNA_COMPLEMENT_TABLE)}
              </span>
            );
          })}
        </div>
      </div>
    );
  };

  renderRight = (): JSX.Element => {
    let bases;

    const rightConstruct = (top: string, bottom: string): JSX.Element => {
      let className;
      if (this.props.activeSide === SIDE_RIGHT) {
        className = CLASS_ACTIVE;
      } else {
        className = CLASS_INACTIVE;
      }
      return (
        <div className={CLASS_INFO_PREVIEW}>
          <div className={className}>{top}</div>
          <div className={className}>{bottom}</div>
        </div>
      );
    };

    const rightSlotSpacer = this.props.rightSlot.spacer();
    if (rightSlotSpacer) {
      const topBases = rightSlotSpacer.slice(0, PREVIEW_EXTRA_BASES);
      return rightConstruct(topBases, util.complement(topBases, DNA_COMPLEMENT_TABLE));
    }

    if (this.props.rightSlot.isEmpty()) {
      return rightConstruct(this._filler(PREVIEW_EXTRA_BASES), this._filler(PREVIEW_EXTRA_BASES));
    }

    const rightFragment = this.props.rightSlot.singleFragment();
    const rightBases = this._fragmentBasesWithCutSite(rightFragment);
    if (this._bothPrimers()) {
      bases = rightBases.slice(0, PREVIEW_PRIMER_BASES);
    } else {
      let offset;
      if (rightFragment.enzymeIDs() != null) {
        ({offset} = this._getRightEnzyme(rightFragment));
      } else {
        offset = 0;
      }
      const sliceStart = Math.abs(offset);
      const sliceEnd = Math.abs(offset) + PREVIEW_EXTRA_BASES;
      bases = rightBases.slice(sliceStart, sliceEnd);
    }
    return rightConstruct(bases, util.complement(bases, DNA_COMPLEMENT_TABLE));
  };

  override render(): JSX.Element {
    return (
      <div className={CLASS_INFO_PREVIEW}>
        {this.renderLeft()}
        {this.renderMid()}
        {this.renderRight()}
      </div>
    );
  }
}

interface ShowFragmentComponentProps {
  activeSlot: AssemblySlotType;
  assembly: Assembly;
  cutsites: Array<Cutsite>;
}

class ShowFragmentComponent extends React.Component<ShowFragmentComponentProps> {
  getPrevSlot = (): AssemblySlotType => {
    const activeIndex = this.props.activeSlot.index()!;
    return this._getSlotAt(activeIndex, -1);
  };

  getNextSlot = (): AssemblySlotType => {
    const activeIndex = this.props.activeSlot.index()!;
    return this._getSlotAt(activeIndex, 1);
  };

  _getSlotAt = (index: number, delta: number): AssemblySlotType => {
    const numSlots = this.props.assembly.slots().length;
    const newIndex = (index + delta + numSlots) % numSlots;
    return this.props.assembly.slots().at(newIndex);
  };

  clearFragment = (): void => {
    this.props.activeSlot.singleFragment().destroy();
  };

  reverseFragment = (): void => {
    this.props.activeSlot.setSingleFragment({reverse: !this.props.activeSlot.singleFragment().reverse()});
  };

  openFragment = (): void => {
    this.props.activeSlot.singleFragment().open();
  };

  renderSitesInsideFragmentWarning = (fragment: __Fragment): JSX.Element | null => {
    if (this.props.assembly.method() !== ASSEMBLY_TYPE.GOLDEN_GATE || fragment.enzymeIDs() == null) {
      return null;
    }
    const fragmentValid =
      _.findWhere(this.props.cutsites, {position: fragment.start()}) != null &&
      _.findWhere(this.props.cutsites, {position: fragment.end()}) != null;
    if (!fragmentValid) {
      // Sequence has changed but fragment has not yet been deleted.
      return null;
    }
    const sitesInsideFragmentWarning = getSitesInsideFragmentWarning(
      fragment.getSequence()!,
      this.props.assembly.enzyme(),
      {start: fragment.start(), end: fragment.end(), cutsites: this.props.cutsites}
    );
    if (sitesInsideFragmentWarning == null) {
      return null;
    }
    return (
      <span className="u-warning-color">
        <PopoverIcon icon="warning" renderContent={() => sitesInsideFragmentWarning} />
      </span>
    );
  };

  renderSitePreviewType = (position: string, slot: AssemblySlotType): JSX.Element => {
    let msg;
    let name;
    if (slot.isSpacer()) {
      msg = 'a spacer (added through a primer)';
      name = 'Spacer';
    } else if (slot.isEmpty()) {
      msg = 'not set yet';
      name = 'Unset';
    } else if (slot.singleFragment().enzymeIDs() != null) {
      msg = 'linearized by digestion';
      name = 'Digest';
    } else {
      msg = 'linearized by PCR';
      name = 'Primer';
    }
    return (
      <Tooltip
        label={`The ${position} fragment is ${msg}.`}
        position="bottom"
        openDelay={500}
        withinPortal={true}
      >
        <span>{name}</span>
      </Tooltip>
    );
  };

  override render(): JSX.Element {
    const fragment = this.props.activeSlot.singleFragment();
    const seqLength = fragment.getSequence()!.length();
    const startIndex = fragment.getSequence()!.startIndex();
    const fragmentLength = sequtil.length(fragment.start()!, fragment.end()!, seqLength);
    const [start, end] = Array.from(
      sequtil.modelRangeToDisplay(fragment.start(), fragment.end(), seqLength, startIndex)
    );
    return (
      <div className="u-small-spacing-top">
        <div className="pull-right">
          <Button.Group className="u-tiny-spacing-bottom u-small-spacing-left">
            <Button onClick={this.clearFragment} size="sm" variant="primary">
              Change
            </Button>
            {/* TODO(BNCH-178139) The styles overrides below can be removed when we have access to all props in Mantine v8 styles defaults callbacks */}
            <Tooltip label="Reverse Fragment" withinPortal={true}>
              <Button
                aria-label="Reverse Fragment"
                onClick={this.reverseFragment}
                leftIcon={<BenchlingIcon icon="swap" />}
                size="sm"
                styles={{leftIcon: {marginRight: '0px'}}}
                variant="tertiary"
              />
            </Tooltip>
            <Tooltip label="Open Fragment" withinPortal={true}>
              <Button
                aria-label="Open Fragment"
                onClick={this.openFragment}
                leftIcon={<BenchlingIcon icon="share-alt" />}
                size="sm"
                styles={{leftIcon: {marginRight: '0px'}}}
                variant="tertiary"
              />
            </Tooltip>
          </Button.Group>
          <div className="text-center faded">
            {`${util.formatBaseCount(fragmentLength)} \u00B7 ${start} to ${end}`}
            {fragment.reverse() ? ' \u00B7 reversed' : undefined}{' '}
            {this.renderSitesInsideFragmentWarning(fragment)}
          </div>
        </div>
        <div className="u-display-inline-block">
          <div className="selectable">
            <SitePreview
              leftSlot={this.getPrevSlot()}
              rightSlot={this.props.activeSlot}
              activeSide={SIDE_RIGHT}
            />
            <div className="current-fragment-info-preview u-small-spacing-left u-small-spacing-right">
              <div className={`${CLASS_ACTIVE} u-small-text text-center`}>
                {`(~${util.formatBaseCount(fragment.length())})`}
              </div>
              <div className={CLASS_ACTIVE}>......</div>
            </div>
            <SitePreview
              leftSlot={this.props.activeSlot}
              rightSlot={this.getNextSlot()}
              activeSide={SIDE_LEFT}
            />
          </div>
          <div className="site-preview-types text-center">
            <div className="pull-left faded">
              {this.renderSitePreviewType('upstream', this.getPrevSlot())}
            </div>
            <div className="pull-right faded">
              {this.renderSitePreviewType('downstream', this.getNextSlot())}
            </div>
            {this.renderSitePreviewType('current', this.props.activeSlot)}
          </div>
        </div>
      </div>
    );
  }
}

export default ShowFragmentComponent;

function __range__(left: number, right: number, inclusive: boolean): Array<number> {
  const range = [];
  const ascending = left < right;
  const end = !inclusive ? right : ascending ? right + 1 : right - 1;
  for (let i = left; ascending ? i < end : i > end; ascending ? i++ : i--) {
    range.push(i);
  }
  return range;
}
